import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AppRoutes from "@/constants/routes/AppRoutes";
import { TEMP_CONVERSATION_ID } from "@/models/conversation/Conversation";
import { useConversationHub } from "@/hooks/useConversationHub";
import { logger } from "@/lib/logger";

/**
 * The id the URL wants opened, or `null` when the address bar names nothing the
 * hub isn't already showing. One shape for the two readers below — the render
 * that decides whether to wait, and the effect that does the opening.
 */
const pendingRestoreId = (
  urlId: string | null,
  selectedId: string | undefined
): string | null => (urlId && urlId !== selectedId ? urlId : null);

export type ChatConversationRouteState = {
  /**
   * A conversation named by the URL is being reopened and nothing is selected
   * yet. Empty-chat UI shown in this window is a lie the user sees flash by.
   */
  isRestoringConversation: boolean;
};

/**
 * Keeps `/chat/:conversationId` and the open conversation in step.
 *
 * The selected conversation lives in `ConversationHubProvider` state, so before
 * this hook any page load dropped it and the user landed in an empty chat (or,
 * via the re-login path, on the dashboard) — LAR-361. With the id in the URL, a
 * reload, a browser-discarded tab and a re-login all restore the conversation,
 * and a chat becomes a linkable address.
 *
 * Returns whether that restore is still in flight, so the view can wait instead
 * of rendering the "new chat" greeting over a conversation that is on its way
 * back (LAR-405).
 */
export const useChatConversationRoute = (): ChatConversationRouteState => {
  const { conversationId } = useParams<{ conversationId?: string }>();
  const { selectedConversation, selectConversationById } = useConversationHub();
  const navigate = useNavigate();
  const { search } = useLocation();

  const selectedId = selectedConversation?.id;
  /**
   * The URL id the two sides were last reconciled to. A mismatch alone cannot say
   * which side moved, and guessing wrong is costly: reading a state-driven
   * mismatch as "the URL wants this conversation" reopens the previous chat and
   * undoes the click that caused it. When the URL still holds this id, the
   * selection is what changed, and the sync below owns it.
   *
   * `undefined` until the first reconcile, so landing on a bare `/chat` (id
   * `null`) is still distinguishable from having reconciled nothing yet.
   */
  const lastUrlIdRef = useRef<string | null | undefined>(undefined);
  /**
   * The id currently being restored, so the URL isn't cleared underneath it.
   * The id rather than a boolean: the hub re-renders often and hands us a new
   * `selectConversationById` each time, so an older restore settling must not
   * unlatch a newer one.
   */
  const restoringIdRef = useRef<string | null>(null);
  /**
   * The same id, in state, because a ref cannot re-render the view that waits on
   * it. Only covers a restore already under way; see `isRestoringConversation`
   * below for the render that precedes it.
   */
  const [restoringId, setRestoringId] = useState<string | null>(null);

  /** Set both copies at once; the ref is what the sync effect below reads. */
  const markRestoring = (id: string | null) => {
    restoringIdRef.current = id;
    setRestoringId(id);
  };

  // URL -> state. Declared before the sync below so it runs first: a fresh load
  // with an id in the address bar latches before anything can rewrite the URL.
  useEffect(() => {
    const urlId = conversationId ?? null;
    // The address bar has not moved since the last reconcile, so any mismatch is
    // the selection having moved. Leave it to the sync effect.
    if (urlId === lastUrlIdRef.current) return;
    lastUrlIdRef.current = urlId;

    // A genuine URL change: first load, a pasted link, or the back button.
    const idToRestore = pendingRestoreId(urlId, selectedId);
    if (!idToRestore) return;

    markRestoring(idToRestore);

    let cancelled = false;
    selectConversationById(idToRestore)
      .catch((error: unknown) => {
        // Deleted, or belongs to someone else: drop the id rather than leave the
        // address bar pointing at a conversation we cannot open. The reconcile
        // above already recorded this id, so it is not retried on the way out.
        logger.warn("Could not restore conversation from URL", {
          operation: "useChatConversationRoute",
          conversationId: idToRestore,
          errorMessage: error instanceof Error ? error.message : String(error),
        });
        if (!cancelled) navigate(`${AppRoutes.chat}${search}`, { replace: true });
      })
      .finally(() => {
        // Not gated on `cancelled`: an effect rerun (the hub re-rendering) tears
        // this closure down without ending the restore, and leaving the latch set
        // would freeze the URL for the rest of the session.
        if (restoringIdRef.current === idToRestore) {
          markRestoring(null);
        }
      });

    return () => {
      cancelled = true;
    };
    // `search` is deliberately excluded: it only feeds the failure redirect, and
    // depending on it would re-run the restore on every filter change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, selectedId, selectConversationById, navigate]);

  // state -> URL. `replace` so switching conversations doesn't stack history
  // entries the back button has to walk through.
  useEffect(() => {
    if (restoringIdRef.current !== null) return;
    if (selectedId === conversationId) return;
    // The placeholder shown while the first message is in flight has no server
    // id yet; the real one arrives moments later and lands in the URL then.
    if (selectedId === TEMP_CONVERSATION_ID) return;

    const target = selectedId
      ? `${AppRoutes.chatConversation(selectedId)}${search}`
      : `${AppRoutes.chat}${search}`;

    navigate(target, { replace: true });
  }, [selectedId, conversationId, search, navigate]);

  // The URL names a conversation the effect above has not picked up yet: this is
  // the render *before* the restore starts. Effects run after the browser has
  // painted, so without this a load or a back button paints the greeting screen
  // for a frame first (LAR-405). Once the effect reconciles, `lastUrlIdRef`
  // holds this id and `restoringId` carries the wait for the rest of the way —
  // which is also why clearing the selection for a new chat reads as false here.
  const idToRestore = pendingRestoreId(conversationId ?? null, selectedId);
  const restoreNotStartedYet =
    idToRestore !== null && idToRestore !== lastUrlIdRef.current;

  return {
    isRestoringConversation: restoreNotStartedYet || restoringId !== null,
  };
};
